"use client";

import React from "react";
import { SpacingType, sectionVariants, ButtonStyleType } from "@/types";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface Hero8Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    hasImage?: boolean;
    image?: string;
    imageAltText?: string;
    hasCodeBox?: boolean;
    codeBox?: string;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero8 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero8Props) => {

  return (
    <SectionWrapper 
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero8"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 8"
    >     
      <div className="container"> 
        <div className="flex flex-col xl:flex-row gap-x-20 items-center">
          <div className="xl:w-[60%] text-center sm:text-left">
            {contents?.badge && (
              <Badge variant="outline" bold className="mb-6">
                {contents?.badge}
              </Badge>
            )}
            {contents?.title && (
              <>
              <h1 className="mb-8">
                {parse(contents.title)}
              </h1>
              </>
            )}
            {contents?.content && (
              <div className="paragraph-lg mx-auto !font-normal">
                {parse(contents?.content)}
              </div>
            )}
            {contents?.hasButton && (
              <div className="mt-8 flex flex-col">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button 
                    size="lg" 
                    variant={contents?.buttonStyle || "primary"}
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={contents?.buttonLink}>
                      {contents?.buttonText}
                    </a>
                  </Button>
                  {contents?.secondaryButton && (
                    <Button
                      variant={contents?.secondaryButtonStyle || "outline"}
                      size="lg"
                      className="w-full gap-2 sm:w-auto"
                      asChild
                    >
                      <a href={contents?.secondaryButtonLink}>
                        {contents?.secondaryButtonText}
                      </a>
                    </Button>
                  )}
                </div>
                {contents?.microCopy && (
                  <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                    {contents?.microCopy}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        {(contents?.hasImage && contents?.image && !contents?.hasCodeBox) && (
          <div className="mt-10 lg:mt-20 aspect-[1150/620] w-full relative">
            <Image src={contents?.image} alt={contents?.imageAltText ?? ""} title={contents?.imageAltText ?? ""} width={1150} height={620} className="h-full w-full rounded-2xl object-cover" priority fetchPriority="high" />
          </div>
        )}
        {(contents?.hasImage && contents?.image && contents?.hasCodeBox && contents?.codeBox) && (
          <div className="relative mt-14 lg:mt-20 grid grid-cols-12 md:gap-12">
          <div className="col-span-12 md:col-span-11">
            <div className="aspect-[1150/620] overflow-clip rounded-2xl shadow-md">
              <Image src={contents?.image} alt={contents?.imageAltText ?? ""} title={contents?.imageAltText ?? ""} width={1150} height={620} className="h-full w-full rounded-2xl object-cover" priority fetchPriority="high" />
            </div>
          </div>
          <div className="absolute inset-0">
            <div className="grid h-full grid-cols-12 md:gap-12">
              <div className="col-span-10 col-start-2 -mt-6 grid translate-y-0 items-start justify-center  md:col-span-6 md:col-end-13 md:-mt-12 md:items-start">
                <div className="bg-primary absolute h-[170px] lg:h-[216px] w-full rounded-lg  p-0 shadow-lg overflow-hidden">
                  <SyntaxHighlighter language="javascript" style={oneDark} className="!p-4 !m-0 text-xs leading-normal [mask-image:linear-gradient(to_right,#000_80%,transparent_100%)] !overflow-hidden h-full">
                    {contents?.codeBox.trim()}
                  </SyntaxHighlighter>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export { Hero8 };
